import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "../contexts/I18nContext";
import { Lock, ArrowLeft, AlertTriangle, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const { t, lang } = useI18n();
  const ar = lang === "ar";
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) {
      toast.error(t("password_too_short"));
      return;
    }
    if (pwd !== pwd2) {
      toast.error(t("passwords_dont_match"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
      setDone(true);
      toast.success(t("password_updated_success"));
      setTimeout(() => nav("/login", { replace: true }), 2200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("invalid_reset_link"));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="mx-auto max-w-md px-5 py-16 text-center" data-testid="reset-no-token" dir={ar ? "rtl" : "ltr"}>
        <div className="h-14 w-14 rounded-full mx-auto inline-flex items-center justify-center bg-red-500/15 border border-red-500/40 mb-4">
          <AlertTriangle className="h-7 w-7 text-red-400" />
        </div>
        <h1 className="text-xl font-black mb-2">{t("invalid_reset_link")}</h1>
        <Link to="/forgot-password" className="btn-gold-outline rounded-full px-5 py-2 text-sm mt-4 inline-block">
          {t("forgot_password_title")}
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-5 py-16" data-testid="reset-password-page" dir={ar ? "rtl" : "ltr"}>
      <div className="flex flex-col items-center gap-3 mb-8">
        <img
          src="/logo192.png"
          alt="ثقة"
          className="h-20 w-20 rounded-full object-cover border border-[#D4AF37]/40 shadow-[0_0_24px_-4px_rgba(212,175,55,0.5)]"
        />
        <h1 className="text-2xl font-black gold-text text-center">{t("reset_password_title")}</h1>
      </div>

      {!done ? (
        <form onSubmit={submit} className="glass rounded-2xl p-7 space-y-4">
          <Input
            label={t("new_password")}
            value={pwd}
            onChange={setPwd}
            testid="reset-pwd"
          />
          <Input
            label={t("confirm_password")}
            value={pwd2}
            onChange={setPwd2}
            testid="reset-pwd2"
          />
          <button
            disabled={loading}
            type="submit"
            className="btn-gold w-full rounded-md py-3 font-bold disabled:opacity-50"
            data-testid="reset-submit"
          >
            {loading ? t("loading") : t("save_new_password")}
          </button>
          <Link
            to="/login"
            className="block text-center text-sm text-white/65 hover:text-[#D4AF37] inline-flex items-center justify-center gap-1 w-full"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {t("back_to_login")}
          </Link>
        </form>
      ) : (
        <div className="glass-gold rounded-2xl p-7 border border-emerald-400/40 text-center space-y-4" data-testid="reset-success">
          <div className="h-14 w-14 rounded-full mx-auto inline-flex items-center justify-center bg-emerald-500/15 border border-emerald-500/40">
            <CheckCircle2 className="h-7 w-7 text-emerald-400" />
          </div>
          <p className="text-white/85 text-sm leading-relaxed">{t("password_updated_success")}</p>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs text-white/70 uppercase tracking-wider">{label}</label>
      <div className="mt-1 relative">
        <Lock className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#D4AF37]" />
        <input
          type="password"
          required
          value={value}
          onChange={(e) => onChange(e.target.value)}
          minLength={6}
          className="w-full bg-black/40 border border-white/10 focus:border-[#D4AF37] focus:outline-none rounded-md ps-9 pe-3 py-2.5 text-sm"
          data-testid={testid}
        />
      </div>
    </div>
  );
}
